/**
 * =============================================================================
 * ROASTBREW - VIDEO VIEW (FAIRPLAY, MOBILE)
 * =============================================================================
 *
 * Mobile-only FairPlay lesson page. Reached when the capability verdict says
 * `player === 'videojs'` AND AppLayout reports a phone-width viewport.
 *
 * ── WHY A SEPARATE FILE ──────────────────────────────────────────────────────
 * VideoViewPageFairPlay.jsx serves EVERY videojs browser, including macOS Safari
 * on a desktop, where its two-column layout works. Rebuilding mobile inside that
 * file would have put a working desktop layout at risk for no benefit. So this is
 * a parallel page and the router picks between them on isMobile. The intended
 * end state is one page with swappable control layouts; until then, a change to
 * the DRM path must be made in BOTH files.
 *
 * ── THE DRM ENGINE BELOW IS COPIED VERBATIM ──────────────────────────────────
 * Everything from `MUXED FAIRPLAY PACKAGE` through the recovery guard was lifted
 * byte-for-byte out of VideoViewPageFairPlay.jsx — NOT retyped. That code was the
 * product of a long, painful debug (muxed SAMPLE-AES package + native HLS +
 * per-track webkitneedkey + the firstWebkitneedkeyTimeout:1 trap). Treat it as
 * frozen. If it must change, change it there first and re-copy.
 *
 * ── WHAT IS DELIBERATELY DIFFERENT FROM THE DESKTOP PAGE ─────────────────────
 * 0. NO PiP button. Reaching fullscreen hands off to Apple's native player,
 *    which carries its own PiP control — and fullscreen -> background is the one
 *    automatic PiP transition WebKit performs. That is strictly better than a web
 *    PiP button, which on iOS needs a live gesture AND a playing video and cannot
 *    be triggered on page-leave at all.
 * 1. FOUR inline controls, not nine. On a ~190px-tall player, nine buttons do not
 *    fit and the tap targets are too small. play/pause + skip, progress, mute,
 *    fullscreen. iOS fullscreen hands off to Apple's NATIVE player, which has
 *    scrubbing, AirPlay and captions built in — so this only has to get you there.
 * 2. NO 4-tab strip. Four nowrap labels needed ~107px each in an 80px slot, which
 *    is what broke the layout. Content is stacked in accordions instead.
 * 3. The answer-key video mounts ONLY while its accordion is open. Two live
 *    FairPlay sessions on one iPhone means two key sessions and two license
 *    round trips on a device with tight decode limits — the exact shape of the
 *    silent-audio bug. One player at a time is an invariant, not a preference.
 * 4. Course navigator lives behind a hamburger as a slide-over sheet.
 * 5. NO landscape CSS coupling: this page does NOT set `data-video-page`, so the
 *    pseudo-fullscreen landscape override in App.jsx never applies to it.
 * 6. Overlays that cover the player are pointerEvents:'none'. The old loading and
 *    error overlays sat at z-20 ABOVE the z-16 controls WITHOUT it, which is why
 *    controls were unclickable whenever isLoading stuck true.
 * 7. `svh` units, not `vh` — iOS `vh` includes the collapsing URL bar, so panels
 *    jumped as it hid.
 * =============================================================================
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { endCardInfo } from './EndOfLessonCard';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-eme';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { isLocked, isLightTheme, tierRank } from './config';
import UpgradePrompt from './UpgradePrompt';
import { videoCompleted, videoPct, nodePct, videoResume } from './data/progress';
import FairPlayPlayer from './FairPlayPlayers';
import { CourseNavigatorView, PhoneNavigatorDrawer, useWindowSize, TitleStack } from './CourseNavigator';
import useCDNToken from './useCDNToken';

// ============================================================================
// END VERBATIM DRM HELPERS
// ============================================================================

// Locate a topic across the catalog by its videoId (the join key).
function findTopicInCatalog(catalog, videoId) {
  for (const su of (catalog || [])) {
    for (const c of (su.courses || [])) {
      for (const ch of (c.chapters || [])) {
        for (const sec of (ch.sections || [])) {
          for (const t of (sec.topics || [])) {
            if (t.videoId === videoId) return { topic: t, course: c, section: sec };
          }
        }
      }
    }
  }
  return null;
}

// ── The pending-lesson shell ─────────────────────────────────────────────────
// Built from the CATALOG ONLY. This used to spread PLACEHOLDER_VIDEO, which meant
// a lesson whose /video/:id request had not landed — or had FAILED — rendered a
// fabricated one: someone else's description, someone else's key topics, and a
// video URL that could never play. On failure that was indistinguishable from a
// real lesson, so an outage looked like working software.
//
// Now every rich field is EMPTY and only the identity is real, taken from the
// catalog the app already holds: the correct title, course and tier appear while
// /video/:id is in flight, and nothing is invented.
//
// SHAPE IS LOAD-BEARING — this must be the complete four-slot object from
// CONTRACT.md §2, not a partial one. Several reads are unguarded:
//   VideoViewPage/FairPlay line ~728   {video.series.name}     — needs series.name
//   VideoViewPage/FairPlay line ~984   {video.keyTopics.map()} — needs keyTopics []
// A partial shell white-screens the player. Keep every key below.
function buildFallbackVideo(catalog, id) {
  const vid = id || '';
  const base = {
    id: vid,
    slug: '',
    title: '',
    description: '',
    keyTopics: [],
    series: { name: '' },
    tierRequired: 'free',
    video:          { id: vid, url: null, thumbnailUrl: null, durationSeconds: 0 },
    homework:       { pdfUrl: null, pages: 0 },
    answerKey:      { pdfUrl: null, pages: 0 },
    answerKeyVideo: { id: vid ? vid + '-ak' : '', url: null, thumbnailUrl: null, durationSeconds: 0 },
  };
  const hit = id ? findTopicInCatalog(catalog, id) : null;
  if (!hit) return base;
  return {
    ...base,
    slug: hit.topic.slug || '',
    title: hit.topic.name || '',
    series: { name: hit.course ? hit.course.name : '' },
    tierRequired: hit.topic.tierRequired,
    video: { ...base.video, thumbnailUrl: hit.topic.thumbnailUrl || null },
  };
}

// ============================================================================
// ACCORDION
// ============================================================================
// Children are rendered ONLY while open. That is load-bearing for the answer-key
// video: it guarantees at most one live FairPlay session on the device. Do not
// change this to keep children mounted and hidden with CSS.
function Section({ title, subtitle, open, onToggle, colors, isLight, children }) {
  return (
    <div style={{
      borderRadius: '12px', marginBottom: '10px', overflow: 'hidden',
      background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)',
      border: `1px solid ${isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)'}`,
    }}>
      <button onClick={onToggle}
        style={{
          width: '100%', minHeight: '56px', padding: '14px 16px', background: 'none', border: 'none',
          display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left',
          fontFamily: '"DM Sans",sans-serif', WebkitTapHighlightColor: 'transparent',
        }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: colors.text }}>{title}</div>
          {subtitle && <div style={{ fontSize: '12px', color: colors.muted, marginTop: '2px' }}>{subtitle}</div>}
        </div>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.muted} strokeWidth="2"
          style={{ flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.25s ease' }}>
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
      {open && <div style={{ padding: '0 16px 16px' }}>{children}</div>}
    </div>
  );
}

const primaryBtn = (colors, isLight) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  minHeight: '48px', padding: '0 20px', width: '100%',
  background: colors.accent, border: 'none', borderRadius: '10px',
  color: isLight ? '#fff' : '#000', fontSize: '14px', fontWeight: 600,
  fontFamily: '"DM Sans",sans-serif', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
});

const secondaryBtn = (colors) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  minHeight: '48px', padding: '0 20px', width: '100%', marginTop: '10px',
  background: 'transparent', border: `1px solid ${colors.accent}`, borderRadius: '10px',
  color: colors.accent, fontSize: '14px', fontWeight: 600,
  fontFamily: '"DM Sans",sans-serif', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
});

// ============================================================================
// COURSE NAVIGATOR SHEET (hamburger)
// ============================================================================
// Full-height slide-over. Chapter -> section -> topic drill-down, one level on
// screen at a time, because three nested lists do not fit a phone.

// ============================================================================
// PAGE
// ============================================================================


// (direct URL, refresh, new tab). Browsers block media from starting WITH SOUND
// until the user has produced a real gesture (click/key/tap) — a hover or page
// load doesn't count. navigator.userActivation.hasBeenActive is a sticky bit that
// is true once the page has been activated and survives in-app (SPA) navigation,
// so if a user clicked a card/link to get here it's already true and we autoplay
// with sound; on a cold load it's false, so we skip autoplay and let the existing
// Play button start it (that click is the gesture that unlocks audio).
function pageHasBeenActivated() {
  const ua = typeof navigator !== 'undefined' ? navigator.userActivation : null;
  return ua && typeof ua.hasBeenActive === 'boolean' ? ua.hasBeenActive : false;
}

export default function VideoViewPageFairPlayMobile() {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { colors, isLight } = useTheme();
  const { profile, catalog, api, refreshAlgorithm, progressByVideo } = useAuth();
  const currentTier = profile?.currentTier || 'free';

  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const courseId = searchParams.get('course');
  const navigate = useNavigate();

  const [video, setVideo] = useState(() => buildFallbackVideo(catalog, id));
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoLocked = isLocked(video.tierRequired, currentTier);
  const actualVideoUrl = (videoLoaded && !videoLocked && video.video && video.video.url) || null;
  const lessonResume = videoResume(progressByVideo, video.id);
  const akResume = videoResume(progressByVideo, video.answerKeyVideo ? video.answerKeyVideo.id : null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [akPlaying, setAkPlaying] = useState(false);
  const [akTime, setAkTime] = useState(0);
  const [navOpen, setNavOpen] = useState(false);

  // ONE open section at a time. This is what keeps the answer-key player from
  // coexisting with the lesson player — see the Section comment.
  const [openSection, setOpenSection] = useState('about');
  const toggle = (k) => setOpenSection((cur) => (cur === k ? null : k));
  // Link refresh (useCDNToken.js): keeps this lesson's CDN links fresh in state. Nothing here waits on it.
  const link = useCDNToken({ id, api, video, setVideo, ready: videoLoaded, isPlaying, currentTime, akPlaying, akTime, akOpen: openSection === 'akvideo' });

  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setVideoLoaded(false);
    api.fetch('/video/' + id)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('video fetch failed'))))
      .then((body) => { if (!cancelled && body && body.data) { setVideo(body.data); setVideoLoaded(true); } })
      .catch(() => { if (!cancelled) { setVideo(buildFallbackVideo(catalogRef.current, id)); setVideoLoaded(false); } });
    return () => { cancelled = true; };
  }, [id, api]);

  const allCourses = (catalog || []).flatMap((su) => su.courses || []);
  const ctxCourse = courseId ? (allCourses.find((c) => c.id === courseId) || null) : null;
  const flatTopics = ctxCourse ? ctxCourse.chapters.flatMap((ch) => ch.sections.flatMap((sec) => sec.topics)) : [];
  const curIdx = flatTopics.findIndex((t) => t.videoId === video.id);
  const upNext = curIdx >= 0 ? (flatTopics[curIdx + 1] || null) : null;

  const goToTopic = (t) => {
    if (isLocked(t.tierRequired, currentTier)) { setUpgradeOpen(true); return; }
    navigate('/video/' + t.videoId + (courseId ? '?course=' + courseId : ''));
  };
  // Autoplay after a lesson change (as on tablets and desktops): Play next lesson, Up Next or a navigator choice
  // starts the new lesson 800 ms after it loads, once the student has tapped the page.
  useEffect(()=>{if(!pageHasBeenActivated())return undefined;const t=setTimeout(()=>setIsPlaying(true),800);return()=>clearTimeout(t);},[video.id]);
  // ── Course Navigator (2.11). Course choice, copied word for word from the desktop page (its lines 227 and 222):
  const coursesForVideo = allCourses.filter(c=>(c.chapters||[]).some(ch=>(ch.sections||[]).some(sec=>(sec.topics||[]).some(t=>t.videoId===video.id))));
  const changeCourse=(cid)=>{ const p=new URLSearchParams(searchParams); if(cid)p.set('course',cid); else p.delete('course'); setSearchParams(p); };
  const [navSeq, setNavSeq] = useState(0);
  const courseBtnRef = useRef(null);
  const [closeAt, setCloseAt] = useState({ top: 16, right: 16 });
  const winSize = useWindowSize();
  // 2.12f · the current lesson's chapter, for the title stack
  const phoneChapter = ctxCourse ? ctxCourse.chapters.find((ch) => ch.sections.some((sec) => sec.topics.some((t) => t.videoId === video.id))) || null : null;
  // Close goes exactly where the Course button is
  const openNav = () => { const b = courseBtnRef.current; if (b) { const r = b.getBoundingClientRect(); setCloseAt({ top: Math.round(r.top), right: Math.round(window.innerWidth - r.right) }); } setNavSeq((n) => n + 1); setNavOpen(true); };
  // End of lesson card: the lesson and course details, plus its two actions (open the navigator; back to the course page)
  const endCard = { ...endCardInfo({ catalog, ctxCourse, curIdx, upNext, video }), onOpenNavigator: openNav, onBackToCourse: () => navigate(endCardInfo({ catalog, ctxCourse, curIdx, upNext, video }).coursePath) };

  // NO AUTOPLAY ON COLD LOAD. Decided earlier and it is the right call on mobile:
  // browsers require a real tap before audible playback, so an autoplay attempt
  // either fails outright or "succeeds" muted — which is how you end up watching a
  // silent lesson. We show a play overlay and wait for the tap instead.
  useEffect(() => {
    setCurrentTime(lessonResume);
    setAkPlaying(false);
    setAkTime(akResume);
    setIsPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id]);

  // One player at a time.
  useEffect(() => { if (isPlaying) setAkPlaying(false); }, [isPlaying]);
  useEffect(() => { if (akPlaying) setIsPlaying(false); }, [akPlaying]);
  useEffect(() => { if (openSection !== 'akvideo') setAkPlaying(false); }, [openSection]);

  // NOTE: this page intentionally does NOT set body[data-video-page]. That
  // attribute only drives the landscape pseudo-fullscreen override in App.jsx,
  // which this layout does not use.

  const refreshAlgoRef = useRef(refreshAlgorithm);
  useEffect(() => { refreshAlgoRef.current = refreshAlgorithm; });
  useEffect(() => () => { if (refreshAlgoRef.current) refreshAlgoRef.current(); }, []);
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    if (refreshAlgoRef.current) refreshAlgoRef.current();
  }, [video.id]);

  const border = isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)';

  return (
    <div style={{
      // 2.12f · the header row shares the top line with AppLayout's own fixed hamburger (top 16 + the safe area, 44 px tall)
      padding: 'calc(16px + env(safe-area-inset-top, 0px)) 12px 40px', minHeight: '100svh', width: '100%',
      boxSizing: 'border-box', overflowX: 'hidden', background: colors.bg,
    }}>
      {/* The page's entrance (October 15, 2026): the same fade-and-rise as the other pages, on the content only. The navigator
          drawer and the upgrade prompt stay outside it: they are fixed to the viewport, and a transformed ancestor would move them. */}
      <div style={{ animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
      {/* 2.12f · header row: shares the top line with AppLayout's own fixed menu button (top 16, left 16, 44 px), with
          the Course button on the right; then the centred title stack (course, chapter, lesson) with arcs */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: '44px', marginBottom: '2px' }}>
        <button ref={courseBtnRef} aria-label="Open Course Navigator" onClick={openNav} aria-hidden={navOpen ? 'true' : undefined} tabIndex={navOpen ? -1 : 0}
          style={{
            height: '44px', padding: '0 14px', flexShrink: 0, borderRadius: '12px', visibility: navOpen ? 'hidden' : 'visible',
            background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${border}`, cursor: 'pointer', color: colors.text,
            display: 'inline-flex', alignItems: 'center', gap: '9px', fontFamily: '"DM Sans",sans-serif', fontWeight: 600, fontSize: '13.5px', WebkitTapHighlightColor: 'transparent',
          }}>
          Course
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="18" r="1" />
          </svg>
        </button>
      </div>
      {/* outside a course: the lesson title alone (October 15, 2026) */}
      <TitleStack course={ctxCourse ? ctxCourse.name : null} chapter={phoneChapter && phoneChapter.name} lesson={video.title} colors={colors} />

      <FairPlayPlayer device="phone" refreshLink={link.refreshCDNToken}
        key={video.id}
        upNext={upNext} upNextLocked={!!upNext && isLocked(upNext.tierRequired, currentTier)} onUpNext={() => { if (upNext) goToTopic(upNext); }} endCard={endCard} onUnlock={() => setUpgradeOpen(true)}
        video={video} colors={colors} currentTier={currentTier}
        videoUrl={actualVideoUrl} videoId={video.id} assetId={video.slug} resumeFrom={lessonResume}
        isPlaying={isPlaying} setIsPlaying={setIsPlaying}
        currentTime={currentTime} setCurrentTime={setCurrentTime}
      />

      {videoLocked && (
        <div style={{ marginTop: '12px', padding: '14px 16px', borderRadius: '12px', background: `${colors.accent}14`, border: `1px solid ${colors.accent}40` }}>
          <p style={{ margin: 0, fontSize: '13px', color: colors.text, fontFamily: '"DM Sans",sans-serif', lineHeight: 1.5 }}>
            This lesson needs a higher plan.
          </p>
        </div>
      )}

      {upNext && (
        <button onClick={() => goToTopic(upNext)}
          style={{
            width: '100%', minHeight: '56px', marginTop: '12px', padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left',
            background: isLight ? `${colors.accent}10` : `${colors.accent}1A`,
            border: `1px solid ${colors.accent}40`, borderRadius: '12px', cursor: 'pointer',
            fontFamily: '"DM Sans",sans-serif', WebkitTapHighlightColor: 'transparent',
          }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, background: colors.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill={isLight ? '#fff' : '#000'} style={{ marginLeft: '2px' }}><path d="M8 5v14l11-7z" /></svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.accent }}>Up next</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{upNext.name}</div>
          </div>
        </button>
      )}

      <div style={{ marginTop: '20px' }}>
        <Section title="About this lesson" open={openSection === 'about'} onToggle={() => toggle('about')} colors={colors} isLight={isLight}>
          <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.7, color: colors.textSecondary, fontFamily: '"DM Sans",sans-serif', whiteSpace: 'pre-line' }}>
            {video.description}
          </p>
          {(video.keyTopics || []).length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {video.keyTopics.map((t, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: colors.text, fontFamily: '"DM Sans",sans-serif', lineHeight: 1.5 }}>
                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: colors.accent, flexShrink: 0, marginTop: '7px' }} />
                  {t}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Homework" subtitle={video.homework?.pages ? `${video.homework.pages} pages` : undefined}
          open={openSection === 'homework'} onToggle={() => toggle('homework')} colors={colors} isLight={isLight}>
          {video.homework?.pdfUrl ? (
            <>
              <button style={primaryBtn(colors, isLight)} onClick={() => link.openPdf('homework')}>
                View assignment
              </button>
              <button style={secondaryBtn(colors)} onClick={() => { /* no submit target in the data yet */ }}>
                Submit work
              </button>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: '13px', color: colors.muted, fontFamily: '"DM Sans",sans-serif' }}>No homework for this lesson.</p>
          )}
        </Section>

        {/* The player here mounts only while this section is open. */}
        <Section title="Answer key video" open={openSection === 'akvideo'} onToggle={() => toggle('akvideo')} colors={colors} isLight={isLight}>
          {video.answerKeyVideo?.url ? (
            <FairPlayPlayer device="phone" refreshLink={link.refreshCDNToken}
              key={'ak-' + video.id}
              video={video} colors={colors} currentTier={currentTier}
              videoUrl={video.answerKeyVideo.url} videoId={video.answerKeyVideo.id}
              assetId={video.slug} resumeFrom={akResume}
              isPlaying={akPlaying} setIsPlaying={setAkPlaying}
              currentTime={akTime} setCurrentTime={setAkTime}
            />
          ) : (
            <p style={{ margin: 0, fontSize: '13px', color: colors.muted, fontFamily: '"DM Sans",sans-serif' }}>No answer-key video for this lesson.</p>
          )}
        </Section>

        <Section title="Answer key (PDF)" subtitle={video.answerKey?.pages ? `${video.answerKey.pages} pages` : undefined}
          open={openSection === 'akpdf'} onToggle={() => toggle('akpdf')} colors={colors} isLight={isLight}>
          {video.answerKey?.pdfUrl ? (
            <button style={primaryBtn(colors, isLight)} onClick={() => link.openPdf('answerKey')}>
              Open answer key
            </button>
          ) : (
            <p style={{ margin: 0, fontSize: '13px', color: colors.muted, fontFamily: '"DM Sans",sans-serif' }}>No answer-key PDF for this lesson.</p>
          )}
        </Section>
      </div>

      </div>

      <PhoneNavigatorDrawer open={navOpen} onClose={() => setNavOpen(false)} width={Math.min(480, Math.round(winSize.width * 0.9))} closeTop={closeAt.top} closeRight={closeAt.right} returnFocusTo={courseBtnRef} colors={colors} light={isLight}>
        <CourseNavigatorView mode="phone" video={video} catalog={catalog} courseId={courseId} ctxCourse={ctxCourse} coursesForVideo={coursesForVideo} progressByVideo={progressByVideo} currentTier={currentTier} colors={colors} isMobile handleSelectTopic={goToTopic} changeCourse={changeCourse} onClose={() => setNavOpen(false)} openSeq={navSeq} />
      </PhoneNavigatorDrawer>

      <UpgradePrompt open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
        @media (hover: none) and (pointer: coarse) { button:hover { opacity: 1; } }
      `}</style>
    </div>
  );
}
